import React, { useEffect, useState } from 'react';
import { FileText, ClipboardCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import {
  ShareModal, MoodInfoModal, SaveTemplateModal, ProfileModal,
  HistorySlidePanel, TeamCollaborationPanel, TeamBoardSlidePanel, ShortcutLegend,
  RestoreSessionDialog,
} from './index';
import { TeamSettings } from '../TeamSettings';
import TutorialSlideshow from '../TutorialSlideshow';
import BugReportModal from '../BugReportModal';
import { ShipVersionModal } from './ShipVersionModal';
import { ConflictModal } from '../../features/conflict-scanner';
import { getFieldInputType, getFieldPlaceholder } from '../../hooks/bracketFieldUtils';

/**
 * RobustDateInput — fixes the "year stuck" bug on native <input type="date">.
 *
 * Bug background (Feb 18, 2026):
 *   Native <input type="date"> fires `onChange` on EVERY year digit typed. Each
 *   onChange wrote a formatted string like "December 15, 0002" back to state,
 *   then `value=` cleared (because it contained a comma), wiping the user's
 *   typing mid-keystroke. Net effect: providers could never type the year.
 *
 * Fix strategy:
 *   - Keep an internal `raw` YYYY-MM-DD that stays controlled by user typing.
 *   - Only "commit" to parent state when the year is >= 1900 (i.e. 4-digit) OR
 *     when the input loses focus, whichever comes first.
 *   - The plain-text companion field still mirrors the parent string so users
 *     can type "January 15, 2025" the long way if they prefer.
 */
const isoToFormatted = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formattedToIso = (formatted) => {
  if (!formatted) return '';
  // already YYYY-MM-DD?
  if (/^\d{4}-\d{2}-\d{2}$/.test(formatted)) return formatted;
  const d = new Date(formatted);
  if (isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  if (yyyy < 1900) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const RobustDateInput = ({ value, onCommit, testId }) => {
  // Local YYYY-MM-DD that the <input type="date"> stays controlled by.
  const [raw, setRaw] = useState(() => formattedToIso(value));

  // If the parent value changes externally (e.g. user typed in the text field),
  // resync the date picker.
  useEffect(() => {
    const next = formattedToIso(value);
    if (next && next !== raw) setRaw(next);
  }, [value, raw]);

  const commit = (iso) => {
    const formatted = isoToFormatted(iso);
    if (formatted) onCommit(formatted);
  };

  return (
    <div className="flex gap-2">
      <Input
        type="date"
        value={raw}
        min="1900-01-01"
        max="2100-12-31"
        onChange={(e) => {
          const next = e.target.value; // YYYY-MM-DD or ""
          setRaw(next);
          // Only commit when we have a 4-digit year >= 1900. This prevents the
          // browser's mid-typing onChange (year=0002, 0020, 0202) from leaking
          // into parent state and wiping the input.
          if (/^\d{4}-\d{2}-\d{2}$/.test(next)) {
            const y = parseInt(next.slice(0, 4), 10);
            if (y >= 1900) commit(next);
          }
        }}
        onBlur={() => {
          // Final-chance commit on blur, in case the user clicked away with a
          // valid 4-digit year still queued only in local state.
          if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            const y = parseInt(raw.slice(0, 4), 10);
            if (y >= 1900) commit(raw);
          }
        }}
        className="h-9 bg-input border-border flex-1"
        data-testid={testId}
      />
      <Input
        placeholder="or type: January 15, 2025"
        value={value || ''}
        onChange={(e) => onCommit(e.target.value)}
        className="h-9 bg-input border-border flex-1"
      />
    </div>
  );
};

/**
 * AppModals — Renders all modal/dialog overlays for MediScribe.
 * Extracted from App.js to reduce monolith size.
 */
const AppModals = ({
  // Share
  shareCode, showShareModal, setShowShareModal, endShareSession,
  effectiveRole, historyShareCode, startShareHistory, startShareSession,
  // Bug Report
  showBugReport, setShowBugReport, guestId,
  // Ship Version (admin)
  showShipVersion, setShowShipVersion,
  // Mood Info
  showMoodInfo, setShowMoodInfo,
  // Save Template
  showSaveTemplateModal, setShowSaveTemplateModal,
  newTemplateName, setNewTemplateName,
  newTemplateDesc, setNewTemplateDesc,
  notes, letterType, specialties, imagingOrders,
  saveAsTemplate,
  // Profile
  showProfileModal, user, setShowProfileModal,
  profileSignature, setProfileSignature,
  profileMTF, setProfileMTF,
  profileRank, setProfileRank,
  defaultMood, setDefaultMood,
  defaultTextSize, setDefaultTextSize,
  defaultIncludeROS, setDefaultIncludeROS,
  defaultIncludePE, setDefaultIncludePE,
  defaultMedicMode, setDefaultMedicMode,
  defaultVisitType, setDefaultVisitType,
  visitSchedule, setVisitSchedule,
  defaultInputMode, setDefaultInputMode,
  personalFirstName, setPersonalFirstName,
  personalLastName, setPersonalLastName,
  personalDOB, setPersonalDOB,
  personalPhone, setPersonalPhone,
  personalOccupation, setPersonalOccupation,
  // v6.8.44 — Saved Medical History / Injuries
  savedInjuries, setSavedInjuries,
  defaultLetterTone, setDefaultLetterTone,
  letterheadLine1, setLetterheadLine1,
  letterheadLine2, setLetterheadLine2,
  letterheadAddress, setLetterheadAddress,
  saveProfile, isSavingProfile,
  clinicalTone, setClinicalTone,
  // Team Settings
  showTeamSettings, setShowTeamSettings,
  // History Slide Panel
  showHistorySlidePanel, setShowHistorySlidePanel,
  history, loadHistoryItem, deleteHistoryItem, loadHistory,
  historyFilter, setHistoryFilter,
  historySearchQuery, setHistorySearchQuery,
  batchSelectedItems, toggleBatchSelect,
  batchDeleteItems, markDraftsAsCompleted, updateHistoryItem,
  textSizeLevel,
  // Team Board
  showTeamBoardPanel, setShowTeamBoardPanel,
  autoShareTeamId, autoShareDrafts,
  toggleAutoShareTeam, toggleAutoShareDrafts,
  setTeamUnreadCount, onLoadTeamNote,
  globalAutoStreamOn,
  // Tutorial
  showTutorialSlideshow, setShowTutorialSlideshow,
  // Team Collab
  showTeamCollab, setShowTeamCollab, onLoadCollabNote,
  patientLabel,
  // Shortcuts
  showShortcutLegend, setShowShortcutLegend,
  // Restore Dialog
  showRestoreDialog, handleRestoreChoice, pendingRestoreData,
  // Bracket Fields Dialog
  showBracketModal, setShowBracketModal,
  bracketFields, setBracketFields,
  setPendingGeneration, handleBracketFieldsComplete,
  // Pre-Gen Modal
  showPreGenModal, setShowPreGenModal,
  preGenFields, setPreGenFields,
  handlePreGenFieldsComplete, skipPreGenAndGenerate,
  // Conflict Modal (Feb 26, 2026) — pre-generate scanner
  showConflictModal, conflicts,
  proceedAfterConflict, dismissConflictModal,
  applyConflictFix,
  // handleDefaultVisitTypeChangeWithSync
  handleDefaultVisitTypeChangeWithSync,
  handleDefaultInputModeChange,
  providerLetterTone: profileProviderLetterTone,
}) => (
  <>
    <ShareModal shareCode={shareCode} showShareModal={showShareModal} setShowShareModal={setShowShareModal} endShareSession={endShareSession} userRole={effectiveRole} historyShareCode={historyShareCode} startShareHistory={startShareHistory} startShareSession={startShareSession} />
    <BugReportModal isOpen={showBugReport} onClose={() => setShowBugReport(false)} guestId={guestId} />
    <MoodInfoModal show={showMoodInfo} onClose={() => setShowMoodInfo(false)} />
    <SaveTemplateModal
      show={showSaveTemplateModal}
      onClose={() => setShowSaveTemplateModal(false)}
      newTemplateName={newTemplateName}
      setNewTemplateName={setNewTemplateName}
      newTemplateDesc={newTemplateDesc}
      setNewTemplateDesc={setNewTemplateDesc}
      notes={notes}
      letterType={letterType}
      specialties={specialties}
      imagingOrders={imagingOrders}
      onSave={saveAsTemplate}
    />
    <ProfileModal
      show={showProfileModal}
      user={user}
      onClose={() => setShowProfileModal(false)}
      profileSignature={profileSignature}
      setProfileSignature={setProfileSignature}
      profileMTF={profileMTF}
      setProfileMTF={setProfileMTF}
      profileRank={profileRank}
      setProfileRank={setProfileRank}
      defaultMood={defaultMood}
      setDefaultMood={setDefaultMood}
      defaultTextSize={defaultTextSize}
      setDefaultTextSize={setDefaultTextSize}
      defaultIncludeROS={defaultIncludeROS}
      setDefaultIncludeROS={setDefaultIncludeROS}
      defaultIncludePE={defaultIncludePE}
      setDefaultIncludePE={setDefaultIncludePE}
      defaultMedicMode={defaultMedicMode}
      setDefaultMedicMode={setDefaultMedicMode}
      defaultVisitType={defaultVisitType}
      setDefaultVisitType={handleDefaultVisitTypeChangeWithSync}
      visitSchedule={visitSchedule}
      setVisitSchedule={setVisitSchedule}
      defaultInputMode={defaultInputMode}
      setDefaultInputMode={handleDefaultInputModeChange}
      personalFirstName={personalFirstName}
      setPersonalFirstName={setPersonalFirstName}
      personalLastName={personalLastName}
      setPersonalLastName={setPersonalLastName}
      personalDOB={personalDOB}
      setPersonalDOB={setPersonalDOB}
      personalPhone={personalPhone}
      setPersonalPhone={setPersonalPhone}
      personalOccupation={personalOccupation}
      setPersonalOccupation={setPersonalOccupation}
      savedInjuries={savedInjuries}
      setSavedInjuries={setSavedInjuries}
      providerLetterTone={profileProviderLetterTone}
      setProviderLetterTone={setDefaultLetterTone}
      letterheadLine1={letterheadLine1}
      setLetterheadLine1={setLetterheadLine1}
      letterheadLine2={letterheadLine2}
      setLetterheadLine2={setLetterheadLine2}
      letterheadAddress={letterheadAddress}
      setLetterheadAddress={setLetterheadAddress}
      userRole={effectiveRole}
      clinicalTone={clinicalTone}
      setClinicalTone={setClinicalTone}
      onSave={saveProfile}
      isSaving={isSavingProfile}
    />
    <TeamSettings isOpen={showTeamSettings} onClose={() => setShowTeamSettings(false)} />
    <HistorySlidePanel
      isOpen={showHistorySlidePanel}
      onClose={() => setShowHistorySlidePanel(false)}
      history={history}
      loadHistoryItem={loadHistoryItem}
      deleteHistoryItem={deleteHistoryItem}
      historyFilter={historyFilter}
      setHistoryFilter={setHistoryFilter}
      historySearchQuery={historySearchQuery}
      setHistorySearchQuery={setHistorySearchQuery}
      batchSelectedItems={batchSelectedItems}
      toggleBatchSelect={toggleBatchSelect}
      batchDeleteItems={batchDeleteItems}
      markDraftsAsCompleted={markDraftsAsCompleted}
      updateHistoryItem={updateHistoryItem}
      loadHistory={loadHistory}
      effectiveRole={effectiveRole}
      textSizeLevel={textSizeLevel}
    />
    <TeamBoardSlidePanel
      isOpen={showTeamBoardPanel}
      onClose={() => setShowTeamBoardPanel(false)}
      user={user}
      guestId={guestId}
      history={history}
      autoShareTeamId={autoShareTeamId}
      autoShareDrafts={autoShareDrafts}
      toggleAutoShareTeam={toggleAutoShareTeam}
      toggleAutoShareDrafts={toggleAutoShareDrafts}
      onUnreadChange={setTeamUnreadCount}
      onLoadNote={onLoadTeamNote}
      globalAutoStreamOn={globalAutoStreamOn}
    />
    {showTutorialSlideshow && (
      <TutorialSlideshow show={true} type={showTutorialSlideshow} onClose={() => setShowTutorialSlideshow(null)} />
    )}
    <TeamCollaborationPanel
      isOpen={showTeamCollab}
      onClose={() => setShowTeamCollab(false)}
      onLoadNote={onLoadCollabNote}
      currentPatientLabel={patientLabel}
      user={user}
      setTeamUnreadCount={setTeamUnreadCount}
    />
    <ShortcutLegend isOpen={showShortcutLegend} onClose={() => setShowShortcutLegend(false)} />
    <RestoreSessionDialog
      open={showRestoreDialog}
      onOpenChange={(open) => { if (!open) handleRestoreChoice('discard'); }}
      pendingRestoreData={pendingRestoreData}
      onRestore={() => handleRestoreChoice('restore')}
      onDone={() => handleRestoreChoice('done')}
      onHistory={() => handleRestoreChoice('history')}
      onDiscard={() => handleRestoreChoice('discard')}
    />

    {/* Bracket Fields Modal */}
    <Dialog open={showBracketModal} onOpenChange={setShowBracketModal}>
      <DialogContent className="sm:max-w-lg bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Complete Letter Details
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Fill in these details to finalize your letter. Fields from your notes that need values.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto custom-scrollbar">
          {Object.keys(bracketFields).map((field) => {
            const inputType = getFieldInputType(field);
            const placeholder = getFieldPlaceholder(field);
            return (
              <div key={field} className="space-y-2">
                <Label className="text-sm text-foreground font-medium capitalize">
                  {field.replace(/_/g, ' ')}
                </Label>
                {inputType === 'date' ? (
                  <RobustDateInput
                    value={bracketFields[field]}
                    onCommit={(val) => setBracketFields(prev => ({ ...prev, [field]: val }))}
                    testId={`bracket-date-${field}`}
                  />
                ) : (
                  <Input
                    type={inputType}
                    placeholder={placeholder}
                    value={bracketFields[field]}
                    onChange={(e) => setBracketFields(prev => ({ ...prev, [field]: e.target.value }))}
                    className="h-9 bg-input border-border"
                  />
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { setShowBracketModal(false); setPendingGeneration(false); }} className="border-border">
            Skip (Keep Brackets)
          </Button>
          <Button onClick={handleBracketFieldsComplete} className="bg-primary hover:bg-primary/90">
            Fill In & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Pre-Generation Modal */}
    <Dialog open={showPreGenModal} onOpenChange={setShowPreGenModal}>
      <DialogContent className="sm:max-w-lg bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-amber-500" />
            Complete Your Notes Before Generating
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Your notes contain placeholders that should be filled in for a better letter. Fill these in now, or skip to generate with placeholders.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto custom-scrollbar">
          {Object.keys(preGenFields).map((field) => {
            const inputType = getFieldInputType(field);
            const placeholder = getFieldPlaceholder(field);
            return (
              <div key={field} className="space-y-2">
                <Label className="text-sm text-foreground font-medium capitalize">
                  {field.replace(/_/g, ' ')}
                </Label>
                {inputType === 'date' ? (
                  <RobustDateInput
                    value={preGenFields[field]}
                    onCommit={(val) => setPreGenFields(prev => ({ ...prev, [field]: val }))}
                    testId={`pregen-date-${field}`}
                  />
                ) : (
                  <Input
                    type={inputType}
                    placeholder={placeholder}
                    value={preGenFields[field]}
                    onChange={(e) => setPreGenFields(prev => ({ ...prev, [field]: e.target.value }))}
                    className="h-9 bg-input border-border"
                  />
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={skipPreGenAndGenerate} className="border-border">
            Skip & Generate Anyway
          </Button>
          <Button onClick={handlePreGenFieldsComplete} className="bg-primary hover:bg-primary/90">
            Fill In & Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ShipVersionModal
      isOpen={!!showShipVersion}
      onClose={() => setShowShipVersion && setShowShipVersion(false)}
    />

    {/* Pre-generate conflict modal (Feb 26, 2026) */}
    <ConflictModal
      open={!!showConflictModal}
      conflicts={conflicts || []}
      onEditNotes={dismissConflictModal}
      onGenerateAnyway={proceedAfterConflict}
      onApplyFix={applyConflictFix}
    />
  </>
);

export default AppModals;
